import { zodResolver } from '@hookform/resolvers/zod';
import { Box, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';

import { useInviteOrganizationAdminValidationSchema } from './schema/invite-organization-admin-validation-schema';

import { useInviteAdmin } from '@/features/organization-admins/hooks/mutation/use-invite-admin';
import { UserRole } from '@/types/user-role';

interface FormValues {
  email: string;
  role: UserRole;
  friendlyName: string;
}

const defaultValues: FormValues = {
  email: '',
  role: 'admin',
  friendlyName: '',
};

const InviteOrganizationAdminDialog = ({ open, onCancel }: DialogActions) => {
  const { t } = useTranslation(['organizationAdmins', 'common']);

  const { mutate, isPending } = useInviteAdmin(onCancel);

  const validationSchema = useInviteOrganizationAdminValidationSchema();

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const onSubmit = (data: FormValues) => {
    mutate({ ...data, isOwner: data.role === 'owner' });
  };

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open]);

  const roleOptions = [
    {
      value: 'owner' as UserRole,
      label: 'common:roles.owner' as const,
    },
    {
      value: 'admin' as UserRole,
      label: 'common:roles.admin' as const,
    },
  ];

  return (
    <Dialog
      open={open}
      title={t('organizationAdmins:dialogTitle')}
      onCancel={onCancel}
      onConfirm={handleSubmit(onSubmit)}
      variant="info"
      isConfirmButtonDisabled={!isValid || !isDirty}
      isConfirmButtonSubmitting={isPending}>
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState: { error } }) => (
          <InputField
            {...field}
            fullWidth
            sx={{ my: 1 }}
            name="inviteEmail"
            label={t('organizationAdmins:email')}
            required
            error={!!error?.message}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        name="role"
        render={({ field: { onChange, value, onBlur } }) => (
          <RadioGroup
            sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2 }}
            onChange={onChange}
            value={value}
            onBlur={onBlur}>
            {roleOptions.map(option => (
              <Box key={option.value}>
                <FormControlLabel
                  label={t(option.label)}
                  control={<Radio value={option.value} />}
                />
              </Box>
            ))}
          </RadioGroup>
        )}
        control={control}
      />
    </Dialog>
  );
};

export default InviteOrganizationAdminDialog;
